import { useEffect, useState } from 'react';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import TextInput from './TextInput';

interface NumberInputProps {
    className?: string;
    title?: string | JSX.Element;
    defaultValue?: number;
    placeholder?: string;
    value?: number | null;
    required?: boolean;
    skipHandleChangeWhenInvalid?: boolean;
    disabled?: boolean;
    // When there is a buffer of time (like a button that must be clicked) before an input
    // calculation happens, we should always run the handleChange event. Otherwise we update
    // the text value only once we have a valid number input.
    automaticRecalculation?: boolean;
    handleTextChange: (value: number) => void;
    handleBlur?: (value: string) => void;
    validation?: RegExp | ((value: string) => boolean);
    errorMessage?: string;
    handleKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    columnNumber?: number;
    testId?: string;
    onValidityChange?: (isValid: boolean) => void;
    track: TrackingEvent;
}

const NumberInput: React.FC<NumberInputProps> = (props: NumberInputProps) => {
    const [textValue, setTextValue] = useState('');

    useEffect(() => {
        if (textValue || props.value === null || props.value === undefined) {
            return;
        }

        setTextValue(`${props.value}`);
    }, [props.value]);

    // For conditional inputs.
    useEffect(() => {
        if (!props.defaultValue) {
            return;
        }

        setTextValue(`${props.defaultValue}`);
    }, [props.defaultValue]);

    useEffect(() => {
        if (props.onValidityChange) {
            const isValid = textValue.trim() === '' ? !props.required : !Number.isNaN(Number(textValue));
            props.onValidityChange(isValid);
        }
    }, [textValue, props.required]);

    return (
        <TextInput
            className={props.className}
            type="number"
            title={props.title}
            placeholder={props.placeholder}
            value={textValue}
            required={props.required}
            onValidityChange={props.onValidityChange}
            skipHandleChangeWhenInvalid={props.skipHandleChangeWhenInvalid}
            disabled={props.disabled}
            handleTextChange={(value) => {
                setTextValue(value);

                const forceHandleChange = props.automaticRecalculation;

                if (!forceHandleChange && (!value || Number.isNaN(+value))) {
                    return;
                }

                props.handleTextChange(+value);
            }}
            handleBlur={props.handleBlur}
            validation={props.validation}
            errorMessage={props.errorMessage}
            handleKeyDown={props.handleKeyDown}
            columnNumber={props.columnNumber}
            testId={props.testId}
            track={props.track}
        />
    );
};

export default NumberInput;
